import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { companyChanged } from 'store/actions/companyActions';
import { AppContextIdName, ReturnCenterDisplaySettingsResponse } from 'returnCenter-api';
import services from 'services';
import type { RootState } from 'store';
import type { ThunkConf } from 'store/thunks';
import axios from 'axios';
import {
    getFromLocalStorage,
    persistToLocalStorage,
    SELECTED_COMPANY_KEY,
    StoredCompanySelection,
} from 'utils/localStorage';

type InitialState = {
    companies: AppContextIdName[] | null;
    companiesLoading: boolean;
    companiesErrorOnLoading: boolean;

    appSettingsLoading?: boolean;

    selectedCompany?: string;
    selectedMarketId?: number;

    appDisplaySettings: ReturnCenterDisplaySettingsResponse | null;
};

const initialState: InitialState = {
    companies: null,
    companiesLoading: false,
    companiesErrorOnLoading: false,

    appSettingsLoading: false,

    selectedCompany: undefined,
    selectedMarketId: undefined,

    appDisplaySettings: null,
};

const companiesSlice = createSlice({
    name: 'companyAndUsers',
    initialState,
    reducers: {
        selectCompanySet: (state, action: PayloadAction<{ contextId: string; marketId?: number }>) => {
            const { contextId, marketId } = action.payload;
            state.selectedCompany = contextId;
            state.selectedMarketId = marketId;
            persistToLocalStorage<StoredCompanySelection>(SELECTED_COMPANY_KEY, { contextId, marketId });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCompanies.pending, (state) => {
            state.companiesLoading = true;
            state.companiesErrorOnLoading = false;
        });
        builder.addCase(getCompanies.fulfilled, (state, action: PayloadAction<AppContextIdName[]>) => {
            state.companies = action.payload;
            state.companiesLoading = false;
            state.companiesErrorOnLoading = false;
        });
        builder.addCase(getCompanies.rejected, (state) => {
            state.companiesLoading = false;
            state.companiesErrorOnLoading = true;
        });
        builder.addCase(getAppDisplaySettings.pending, (state) => {
            state.appSettingsLoading = true;
        });
        builder.addCase(getAppDisplaySettings.rejected, (state) => {
            state.appSettingsLoading = false;
        });
        builder.addCase(
            getAppDisplaySettings.fulfilled,
            (state, action: PayloadAction<ReturnCenterDisplaySettingsResponse>) => {
                state.appDisplaySettings = action.payload;
                state.appSettingsLoading = false;
            },
        );

        // Partial reset on company change — keep company list, update selection
        builder.addCase(companyChanged, (state, action) => {
            const { contextId, marketId } = action.payload;
            state.selectedCompany = contextId;
            state.selectedMarketId = marketId;
            state.appDisplaySettings = null;
            state.appSettingsLoading = false;
            persistToLocalStorage<StoredCompanySelection>(SELECTED_COMPANY_KEY, { contextId, marketId });
        });
    },
});

const getCompanies = createAsyncThunk<AppContextIdName[], void, ThunkConf>(
    'order/companies',
    async (_, { rejectWithValue }) => {
        try {
            const response = await services.order.orderGetCompanies();
            return response;
        } catch (error) {
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

const initializeCompany = createAsyncThunk<string | null | undefined, AppContextIdName[], ThunkConf>(
    'company/initializeCompany',
    async (companies, { dispatch }) => {
        try {
            const stored = getFromLocalStorage<StoredCompanySelection>(SELECTED_COMPANY_KEY);

            // Find a matching company+market combo, fall back to first company
            const storedMatch = companies.find(
                (c) => c.id === stored?.contextId && (c.marketId ?? undefined) === (stored?.marketId ?? undefined),
            );
            const companyToUse = storedMatch ?? companies[0];
            const companyIdToUse = companyToUse?.id ?? '';
            const marketIdToUse = companyToUse?.marketId ?? undefined;

            dispatch(selectCompanySet({ contextId: companyIdToUse, marketId: marketIdToUse }));

            return companyIdToUse;
        } catch (error) {
            console.warn('Error initializing company:', error);
        }
    },
);

const getAppDisplaySettings = createAsyncThunk<ReturnCenterDisplaySettingsResponse, void, ThunkConf>(
    'display/settings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await services.order.orderGetAppDisplaySettings();
            return response;
        } catch (error) {
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

const selectCompanies = (state: RootState) => state.companies.companies;
const selectedCompany = (state: RootState) => state.companies.selectedCompany;
const selectDisplaySettings = (state: RootState) => state.companies.appDisplaySettings;
const selectAppSettingsLoading = (state: RootState) => state.companies.appSettingsLoading;
const selectCompaniesLoading = (state: RootState) => state.companies.companiesLoading;
const selectCompaniesErrorOnLoading = (state: RootState) => state.companies.companiesErrorOnLoading;

const selectSelectedMarketId = (state: RootState) => state.companies.selectedMarketId;

const selectCurrentCompany = createSelector(
    selectCompanies,
    selectedCompany,
    selectSelectedMarketId,
    (companies, selected, marketId) => {
        return companies?.find(
            (company) => company.id === selected && (company.marketId ?? undefined) === (marketId ?? undefined),
        );
    },
);

export const companyThunks = {
    getCompanies,
    getAppDisplaySettings,
    initializeCompany,
};

export const companiesSelector = {
    selectCompanies,
    selectedCompany,
    selectCurrentCompany,
    selectDisplaySettings,
    selectAppSettingsLoading,
    selectCompaniesLoading,
    selectCompaniesErrorOnLoading,
    selectSelectedMarketId,
};

export const { selectCompanySet } = companiesSlice.actions;

export default companiesSlice.reducer;
